import { BillingAddressInput } from 'lib/types'
import { FetchResult } from '@apollo/client'
import { replaceMutation, ServiceDependencies } from 'lib/core/services/magento'
import { EMPTY_STRING } from 'lib/core/resources/constants'
import { apolloClient } from 'lib/core/services/magento/client'
import gql from 'graphql-tag'
import { jsonToGraphQLQuery } from 'lib/core/resources/helpers/jsonToGraphql'

export interface SetBillingAddressOnCartMutationObject {
  mutation: {
    setBillingAddressOnCart: {
      __args: {
        input: {
          cart_id: string
          billing_address: {
            customer_address_id?: number
            same_as_shipping?: boolean
            address?: {
              firstname: string
              lastname: string
              company: string
              street: string[]
              city: string
              region: string
              postcode: string
              country_code: string
              telephone: string
              save_in_address_book: boolean
              [key: string]: any
            }
            [key: string]: any
          }
        }
      }
      [key: string]: any
    }
  }
}

export const setBillingAddressOnCartMutationObject: SetBillingAddressOnCartMutationObject = {
  mutation: {
    setBillingAddressOnCart: {
      __args: {
        input: {
          cart_id: '',
          billing_address: {
            customer_address_id: 0,
            same_as_shipping: false,
            address: {
              firstname: '',
              lastname: '',
              company: '',
              street: [],
              city: '',
              region: '',
              postcode: '',
              country_code: '',
              telephone: '',
              save_in_address_book: false
            }
          }
        }
      }
    }
  }
}

interface SetBillingAddressOnCartMutationInput {
  cartId: string
  address?: BillingAddressInput
  addressId?: number
  sameAsShipping?: boolean
  [key: string]: any
}

export type SetBillingAddressOnCartMutation = (input: SetBillingAddressOnCartMutationInput) => Promise<FetchResult<any>>

export const createSetBillingAddressOnCartMutation = ({
  getMutations,
  getQueries
}: ServiceDependencies): SetBillingAddressOnCartMutation => async ({
  cartId,
  address,
  addressId,
  sameAsShipping,
  ...restInputParams
}) => {
  const queries = getQueries()
  const {
    firstName,
    lastName,
    company,
    street,
    street2,
    city,
    regionCode,
    postalCode,
    countryCode,
    phoneNumber,
    saveInAddressBook,
    ...rest
  } = address ?? {}

  const setBillingAddressOnCartMutation = getMutations().setBillingAddressOnCartMutation
  const { mutation } = setBillingAddressOnCartMutation

  for (const key in restInputParams) {
    mutation.setBillingAddressOnCart.__args.input.billing_address[key] = restInputParams[key]
  }

  mutation.setBillingAddressOnCart.__args.input.cart_id = cartId
  if (addressId) {
    mutation.setBillingAddressOnCart.__args.input.billing_address.customer_address_id = addressId
  }

  const billingAddress = mutation.setBillingAddressOnCart.__args.input.billing_address.address
  if (billingAddress) {
    billingAddress.firstname = firstName ?? EMPTY_STRING
    billingAddress.lastname = lastName ?? EMPTY_STRING
    billingAddress.company = company ?? EMPTY_STRING
    billingAddress.street = [street ?? EMPTY_STRING, street2 ?? '']
    billingAddress.city = city ?? EMPTY_STRING
    billingAddress.region = regionCode ?? EMPTY_STRING
    billingAddress.postcode = postalCode ?? EMPTY_STRING
    billingAddress.country_code = countryCode ?? EMPTY_STRING
    billingAddress.telephone = phoneNumber ?? EMPTY_STRING
    billingAddress.save_in_address_book = saveInAddressBook ?? false
  }

  mutation.setBillingAddressOnCart.__args.input.billing_address.same_as_shipping = sameAsShipping ?? false

  mutation.setBillingAddressOnCart.cart = {
    billing_address: queries.billingAddresses.billing_address
  }

  if (sameAsShipping) {
    delete mutation.setBillingAddressOnCart.__args.input.billing_address.address
    delete mutation.setBillingAddressOnCart.__args.input.billing_address.customer_address_id
  } else {
    delete mutation.setBillingAddressOnCart.__args.input.billing_address.same_as_shipping
    if (addressId) {
      delete mutation.setBillingAddressOnCart.__args.input.billing_address.address
    } else {
      delete mutation.setBillingAddressOnCart.__args.input.billing_address.customer_address_id
    }
  }

  return apolloClient.mutate({
    // TODO: remove "replaceMutation"; see src/lib/core/services/magento/mutations/setShippingAddressesOnCart/index.ts
    // TODO: search for all replaceMutation and remove.
    mutation: gql`${replaceMutation(jsonToGraphQLQuery(setBillingAddressOnCartMutation), rest)}`,
    fetchPolicy: 'no-cache'
  })
}
